import React, { useState, useEffect } from 'react';
import { customerService } from '../services/customerService';
import CustomerModal from './CustomerModal';

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
  'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
  'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
  'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq',
  'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
  'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho',
  'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania',
  'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
  'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia',
  'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea',
  'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
  'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe',
];

const SiteModal = ({ site, onSave, onClose, defaultCustomerId }) => {
  const [formData, setFormData] = useState({
    customer_id: defaultCustomerId || '',
    name: '',
    street_address: '',
    town: '',
    county: '',
    country: 'United Kingdom',
    postcode: '',
  });
  const [customers, setCustomers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);

  const isEditing = !!site;

  useEffect(() => {
    customerService.getCustomers().then(res => setCustomers((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);

  useEffect(() => {
    if (site) {
      setFormData({
        customer_id: site.customer_id || '',
        name: site.name || '',
        street_address: site.street_address || '',
        town: site.town || '',
        county: site.county || '',
        country: site.country || 'United Kingdom',
        postcode: site.postcode || '',
      });
    }
  }, [site]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      await onSave(formData);
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'An error occurred. Please try again.' });
      }
    }

    setLoading(false);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
      errors[field]
        ? 'border-red-500 focus:border-red-500'
        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
    }`;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Site' : 'Add New Site'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl font-semibold leading-none transition-colors"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {errors.general}
            </div>
          )}

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">
              Customer <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                className={inputClass('customer_id') + ' flex-1'}
              >
                <option value="">Select a customer...</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewCustomerModal(true)}
                className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap"
              >+ New</button>
            </div>
            {errors.customer_id && <p className="text-red-500 dark:text-red-400 text-sm">{errors.customer_id[0]}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">
              Site Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Site name"
              className={inputClass('name')}
            />
            {errors.name && <p className="text-red-500 dark:text-red-400 text-sm">{errors.name[0]}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">
              Street Address
            </label>
            <input
              type="text"
              name="street_address"
              value={formData.street_address}
              onChange={handleChange}
              placeholder="Street address"
              className={inputClass('street_address')}
            />
            {errors.street_address && <p className="text-red-500 dark:text-red-400 text-sm">{errors.street_address[0]}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                Town
              </label>
              <input
                type="text"
                name="town"
                value={formData.town}
                onChange={handleChange}
                placeholder="Town"
                className={inputClass('town')}
              />
              {errors.town && <p className="text-red-500 dark:text-red-400 text-sm">{errors.town[0]}</p>}
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                County
              </label>
              <input
                type="text"
                name="county"
                value={formData.county}
                onChange={handleChange}
                placeholder="County"
                className={inputClass('county')}
              />
              {errors.county && <p className="text-red-500 dark:text-red-400 text-sm">{errors.county[0]}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                Postcode
              </label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                placeholder="Postcode"
                className={inputClass('postcode')}
              />
              {errors.postcode && <p className="text-red-500 dark:text-red-400 text-sm">{errors.postcode[0]}</p>}
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                Country <span className="text-red-500">*</span>
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={inputClass('country')}
              >
                <option value="">Select a country...</option>
                {COUNTRIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {errors.country && <p className="text-red-500 dark:text-red-400 text-sm">{errors.country[0]}</p>}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Site')}
            </button>
          </div>
        </form>
      </div>
    </div>

    {showNewCustomerModal && (
      <CustomerModal
        onSave={async (data) => {
          const res = await customerService.createCustomer(data);
          const newCustomer = res.data;
          const updated = await customerService.getCustomers();
          setCustomers((updated.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
          setFormData(prev => ({ ...prev, customer_id: String(newCustomer.id) }));
          setShowNewCustomerModal(false);
        }}
        onClose={() => setShowNewCustomerModal(false)}
      />
    )}
    </>
  );
};

export default SiteModal;
